import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  ElectricalServices as PlugIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  { id: 'introduccion', label: 'Introducción' },
  { id: 'configuracion', label: 'Configuración' },
  { id: 'autenticacion', label: 'Autenticación y Seguridad' },
  { id: 'peticiones', label: 'Estructura de Peticiones' },
  { id: 'eventos', label: 'Eventos Disponibles' },
  { id: 'respuestas', label: 'Manejo de Respuestas' },
  { id: 'ejemplos', label: 'Ejemplos de Implementación' },
  { id: 'testing', label: 'Testing y Debugging' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

const PAYLOAD_EXAMPLE = `{
  "event": "pedido.created",
  "timestamp": "2026-08-14T12:00:00.0000000Z",
  "tenantId": 1,
  "idCompania": "1",
  "data": { },
  "previousData": { },
  "changes": [],
  "trigger": "created"
}`;

const NODE_VERIFY = `const crypto = require('crypto');

function verify(rawBody, signatureHeader, secret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signatureHeader)
  );
}`;

const NODE_EXPRESS = `const express = require('express');
const app = express();

app.post(
  '/webhooks/eone',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const signature = req.headers['x-webhook-signature'];
    if (!verify(req.body, signature, process.env.EONE_WEBHOOK_SECRET)) {
      return res.status(401).send('Firma inválida');
    }
    const event = JSON.parse(req.body.toString('utf8'));
    // Procesá el evento y respondé 2xx lo antes posible.
    res.status(200).send('ok');
  }
);`;

const PYTHON_VERIFY = `import hmac
import hashlib

def verify(raw_body: bytes, signature_header: str, secret: str) -> bool:
    expected = "sha256=" + hmac.new(
        secret.encode("utf-8"), raw_body, hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature_header)`;

const EVENTOS_FASE1: { grupo: string; eventos: { code: string; label: string }[] }[] = [
  {
    grupo: 'Clientes',
    eventos: [
      { code: 'cliente.created', label: 'Cliente creado' },
      { code: 'cliente.updated', label: 'Cliente actualizado' },
      { code: 'cliente.deleted', label: 'Cliente eliminado' },
    ],
  },
  {
    grupo: 'Productos',
    eventos: [
      { code: 'producto.created', label: 'Producto creado' },
      { code: 'producto.updated', label: 'Producto actualizado' },
      { code: 'producto.deleted', label: 'Producto eliminado' },
      { code: 'producto.precio.updated', label: 'Precio de producto actualizado' },
    ],
  },
  {
    grupo: 'Pedidos',
    eventos: [
      { code: 'pedido.created', label: 'Pedido de venta creado' },
      { code: 'pedido.updated', label: 'Pedido de venta actualizado' },
      { code: 'pedido.anulado', label: 'Pedido de venta anulado' },
      { code: 'pedido.status_changed', label: 'Estado de pedido cambiado' },
      { code: 'pedido.facturado', label: 'Pedido facturado' },
    ],
  },
  {
    grupo: 'Facturas',
    eventos: [
      { code: 'factura.created', label: 'Factura creada' },
      { code: 'factura.updated', label: 'Factura actualizada' },
      { code: 'factura.anulado', label: 'Factura anulada' },
      { code: 'factura.cae.obtenido', label: 'CAE obtenido en factura' },
    ],
  },
  {
    grupo: 'Remitos',
    eventos: [
      { code: 'remito.created', label: 'Remito creado' },
      { code: 'remito.anulado', label: 'Remito anulado' },
    ],
  },
  {
    grupo: 'Cobros',
    eventos: [
      { code: 'cobro.created', label: 'Cobro/recibo creado' },
      { code: 'cobro.anulado', label: 'Cobro/recibo anulado' },
      { code: 'cobro.aplicado', label: 'Cobro aplicado a comprobantes' },
    ],
  },
];

const EVENTOS_PROXIMOS = [
  'cotizacion.created | updated | anulado',
  'nota_credito.created | cae.obtenido',
  'devolucion.created',
];

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#0f172a',
        color: '#e2e8f0',
        borderRadius: 2,
        overflow: 'hidden',
        my: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.75, bgcolor: '#1e293b', borderBottom: '1px solid #334155' }}
      >
        <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace', letterSpacing: 0.4 }}>
          {language ?? 'json'}
        </Typography>
        <Tooltip title={copied ? 'Copiado' : 'Copiar'}>
          <IconButton size="small" onClick={() => void copy()} sx={{ color: '#cbd5e1' }}>
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          overflow: 'auto',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 13,
          lineHeight: 1.65,
          whiteSpace: 'pre',
        }}
      >
        {code}
      </Box>
    </Box>
  );
};

const EntityBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      flex: 1,
      textAlign: 'center',
      py: 1.25,
      px: 1.5,
      borderRadius: 1.5,
      bgcolor: '#e8eaf6',
      color: '#312e81',
      fontWeight: 600,
      fontSize: 13,
    }}
  >
    {children}
  </Box>
);

const StepBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      py: 1.1,
      px: 1.5,
      borderRadius: 1.5,
      bgcolor: '#fff8e1',
      color: '#78350f',
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.45,
      border: '1px solid #fde68a',
    }}
  >
    {children}
  </Box>
);

const FlowDiagram: React.FC = () => (
  <Box
    sx={{
      my: 3,
      p: { xs: 2, md: 3 },
      borderRadius: 2,
      border: '1px solid #e5e7eb',
      bgcolor: '#fafafa',
    }}
  >
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
      <EntityBox>eOne</EntityBox>
      <EntityBox>Internet</EntityBox>
      <EntityBox>Tu sistema</EntityBox>
    </Stack>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 16px 1fr 16px 1fr' },
        gap: 1.5,
        alignItems: 'start',
      }}
    >
      <Stack spacing={1.25}>
        <StepBox>1. Ocurre un evento (ej. pedido creado)</StepBox>
        <StepBox>2. eOne arma el payload y lo firma con HMAC-SHA256</StepBox>
        <StepBox>3. Envía un POST JSON a tu URL</StepBox>
      </Stack>
      <Box sx={{ display: { xs: 'none', md: 'block' }, alignSelf: 'center', height: '70%', borderRight: '2px dashed #cbd5e1' }} />
      <Stack spacing={1.25} justifyContent="center" sx={{ pt: { md: 6 } }}>
        <StepBox>El cuerpo viaja por HTTPS. Si hay secret, viaja también X-Webhook-Signature.</StepBox>
      </Stack>
      <Box sx={{ display: { xs: 'none', md: 'block' }, alignSelf: 'center', height: '70%', borderRight: '2px dashed #cbd5e1' }} />
      <Stack spacing={1.25}>
        <StepBox>4. Tu endpoint verifica la firma sobre el body crudo</StepBox>
        <StepBox>5. Procesás el evento y respondés HTTP 2xx</StepBox>
        <StepBox>6. Si no hay 2xx, eOne reintenta con backoff exponencial</StepBox>
      </Stack>
    </Box>
  </Box>
);

const SectionTitle: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography
    id={id}
    component="h2"
    variant="h5"
    sx={{
      fontWeight: 700,
      color: '#1e293b',
      mt: 5,
      mb: 2,
      scrollMarginTop: 96,
      letterSpacing: -0.3,
    }}
  >
    {children}
  </Typography>
);

const SubTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h3"
    variant="h6"
    sx={{ fontWeight: 700, color: '#334155', mt: 3.5, mb: 1.25, fontSize: '1.05rem' }}
  >
    {children}
  </Typography>
);

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.75, mb: 1.5 }}>
    {children}
  </Typography>
);

const Bullet: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box component="li" sx={{ mb: 1.1, color: '#475569', lineHeight: 1.7 }}>
    <Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>
      {title}:{' '}
    </Box>
    {children}
  </Box>
);

const WebhooksDocumentacion: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [active, setActive] = useState<SectionId>(SECTIONS[0].id);

  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const nextId = visible?.target.id as SectionId | undefined;
        if (nextId && SECTIONS.some((s) => s.id === nextId)) setActive(nextId);
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: [0.1, 0.25, 0.5] },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goTo = useCallback((id: SectionId) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const navItem = (section: (typeof SECTIONS)[number], index: number) => {
    const selected = active === section.id;
    return (
      <Box
        key={section.id}
        component="button"
        type="button"
        onClick={() => goTo(section.id)}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          border: 0,
          cursor: 'pointer',
          py: 1,
          px: 1.5,
          borderRadius: 1,
          bgcolor: selected ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
          borderLeft: selected ? '3px solid #1976d2' : '3px solid transparent',
          color: selected ? '#1565c0' : '#475569',
          fontWeight: selected ? 600 : 400,
          fontSize: 14,
          lineHeight: 1.4,
          '&:hover': { bgcolor: selected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(15, 23, 42, 0.04)' },
        }}
      >
        {index + 1}. {section.label}
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh', mx: { xs: -2, sm: -3 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
          alignItems: 'start',
          minHeight: '100vh',
        }}
      >
        <Box
          component="nav"
          aria-label="Secciones de documentación"
          sx={{
            bgcolor: '#f8fafc',
            borderRight: { md: '1px solid #e5e7eb' },
            borderBottom: { xs: '1px solid #e5e7eb', md: 0 },
            px: 2,
            py: 2.5,
            position: { md: 'sticky' },
            top: 0,
            maxHeight: { md: '100vh' },
            overflow: 'auto',
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/configuraciones/webhooks')}
            sx={{ textTransform: 'none', mb: 2, color: '#475569' }}
          >
            Volver a Webhooks
          </Button>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5, mb: 0.5 }}>
            <PlugIcon sx={{ color: '#0f172a' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: '#0f172a' }}>Webhooks</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: '#64748b', px: 0.5, mb: 2 }}>
            Documentación Técnica
          </Typography>
          {isMdUp ? (
            <Stack spacing={0.25}>{SECTIONS.map((s, i) => navItem(s, i))}</Stack>
          ) : (
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
              {SECTIONS.map((s, i) => (
                <Chip
                  key={s.id}
                  label={`${i + 1}. ${s.label}`}
                  onClick={() => goTo(s.id)}
                  color={active === s.id ? 'primary' : 'default'}
                  variant={active === s.id ? 'filled' : 'outlined'}
                  sx={{ flexShrink: 0 }}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Box sx={{ px: { xs: 2.5, md: 6 }, py: { xs: 3, md: 5 }, maxWidth: 880 }}>
          <Typography variant="overline" sx={{ color: '#1976d2', fontWeight: 700, letterSpacing: 1.2 }}>
            Integraciones
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: -0.6, mb: 1.5, fontSize: { xs: '1.75rem', md: '2.15rem' } }}>
            Webhooks outbound
          </Typography>
          <Body>
            Notificaciones HTTP en tiempo real que eOne envía a tu sistema cuando ocurren eventos de negocio.
            Cada entrega puede firmarse con HMAC-SHA256, se reintenta si falla y queda registrada en logs.
          </Body>

          <SectionTitle id="introduccion">1. Introducción</SectionTitle>
          <Body>
            Un webhook es una llamada HTTP que eOne inicia hacia una URL tuya. A diferencia de los tokens de
            integración, no tenés que consultar la API en un loop: eOne te avisa cuando algo cambia.
          </Body>

          <TableContainer sx={{ my: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tokens de integración</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Webhooks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Dirección</TableCell>
                  <TableCell>Tu sistema llama a eOne (`api/integration/*`)</TableCell>
                  <TableCell>eOne llama a tu URL</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Auth</TableCell>
                  <TableCell>JWT Bearer de larga vida</TableCell>
                  <TableCell>Firma HMAC opcional en cada POST</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <SubTitle>Casos de uso</SubTitle>
          <Body>Los webhooks sirven para reaccionar a cambios de eOne sin polling.</Body>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            <Bullet title="Sincronización de datos">
              Mantener tu CRM, ecommerce o WMS alineado cuando se crea o actualiza un cliente, producto o pedido.
            </Bullet>
            <Bullet title="Automatización de procesos">
              Disparar flujos internos al facturar, obtener CAE, anular un comprobante o aplicar un cobro.
            </Bullet>
            <Bullet title="Integración con sistemas externos">
              Recibir el mismo evento en un middleware, iPaaS o cola propia y repartirlo a varios destinos.
            </Bullet>
            <Bullet title="Notificaciones en tiempo real">
              Avisar a otro equipo o servicio apenas ocurre el hecho, sin esperar una consulta periódica.
            </Bullet>
          </Box>

          <SubTitle>Flujo general de funcionamiento</SubTitle>
          <FlowDiagram />

          <SectionTitle id="configuracion">2. Configuración</SectionTitle>
          <Body>
            Desde Administración → Webhooks (`/configuraciones/webhooks`) creás una suscripción. Se requiere rol
            Admin o SUPERADMIN.
          </Body>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            <Bullet title="URL">
              Endpoint HTTPS que recibirá POST JSON. Es el único destino de esa suscripción.
            </Bullet>
            <Bullet title="Eventos">
              Elegí uno o más eventos del catálogo. Solo se envían los marcados como emitibles.
            </Bullet>
            <Bullet title="Secret HMAC">
              Clave opcional. Si la cargás, eOne firma cada body y envía `X-Webhook-Signature`.
            </Bullet>
            <Bullet title="Reintentos y timeout">
              Máximo de intentos (default 3, entre 1 y 10) y timeout en ms (default 30000, entre 1s y 120s).
            </Bullet>
            <Bullet title="Headers custom">
              Objeto JSON extra, por ejemplo {'`{"Authorization":"Bearer ..."}`'}. No pises Content-Type.
            </Bullet>
            <Bullet title="Email de alerta">
              Si la entrega agota los reintentos, eOne puede avisarte por correo (si el tenant tiene mail configurado).
            </Bullet>
            <Bullet title="Filtros">
              Reglas sobre campos del documento (`asentado`, `nroCliente`, `idCompania`, `total`, etc.) con match
              AND (`all`) u OR (`any`). Si no cumplen, el log queda `filtered_out` y no se llama a tu URL.
            </Bullet>
          </Box>

          <SectionTitle id="autenticacion">3. Autenticación y Seguridad</SectionTitle>
          <Body>
            Si configurás un secret, cada POST incluye:
          </Body>
          <CodeBlock language="http" code="X-Webhook-Signature: sha256=<hex>" />
          <Body>
            La firma es HMAC-SHA256 del body JSON crudo (UTF-8), con el secret como clave. El valor es hex en
            minúsculas, prefijado con `sha256=`. Verificá siempre el body original: si parseás a JSON y lo
            volvés a serializar, la firma no va a coincidir.
          </Body>
          <Body>
            Compará con una función de tiempo constante (`timingSafeEqual` / `compare_digest`). Tratá el secret
            como una contraseña: no lo expongas en logs ni en el frontend.
          </Body>
          <CodeBlock language="javascript" code={NODE_VERIFY} />

          <SectionTitle id="peticiones">4. Estructura de Peticiones</SectionTitle>
          <Body>eOne envía `POST` con `Content-Type: application/json` y `User-Agent: eOne-Webhook/1.0`.</Body>
          <SubTitle>Payload</SubTitle>
          <CodeBlock language="json" code={PAYLOAD_EXAMPLE} />
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            <Bullet title="event">Nombre del evento, por ejemplo `pedido.created`.</Bullet>
            <Bullet title="timestamp">Momento del disparo en UTC (ISO 8601).</Bullet>
            <Bullet title="tenantId">Identificador del tenant que originó el evento.</Bullet>
            <Bullet title="idCompania">Compañía fiscal cuando aplica; puede omitirse.</Bullet>
            <Bullet title="data">Estado actual del recurso (campos en camelCase).</Bullet>
            <Bullet title="previousData">Estado anterior en actualizaciones, si está disponible.</Bullet>
            <Bullet title="changes">Lista de campos que cambiaron (`field`, `path`, `oldValue`, `newValue`).</Bullet>
            <Bullet title="trigger">Motivo de negocio (`created`, `updated`, etc.).</Bullet>
          </Box>

          <SectionTitle id="eventos">5. Eventos Disponibles</SectionTitle>
          <Body>Fase 1 (emitidos hoy). El alta del webhook lista el catálogo vivo, incluyendo los marcados como “próximamente”.</Body>
          {EVENTOS_FASE1.map((g) => (
            <Box key={g.grupo} sx={{ mb: 2.5 }}>
              <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>{g.grupo}</Typography>
              <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Evento</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.eventos.map((ev) => (
                      <TableRow key={ev.code}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{ev.code}</TableCell>
                        <TableCell>{ev.label}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
          <SubTitle>Próximamente</SubTitle>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            {EVENTOS_PROXIMOS.map((e) => (
              <Typography key={e} component="li" sx={{ mb: 0.75, color: '#475569', fontFamily: 'monospace', fontSize: 14 }}>
                {e}
              </Typography>
            ))}
          </Box>

          <SectionTitle id="respuestas">6. Manejo de Respuestas</SectionTitle>
          <Body>
            eOne considera éxito cualquier HTTP 2xx. Cualquier otro código, timeout o error de red entra en
            reintento.
          </Body>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            <Bullet title="Backoff">
              Espera `min(1000 × 2^(intento-1), 30000)` milisegundos entre intentos (1s, 2s, 4s… hasta 30s).
            </Bullet>
            <Bullet title="Estados de log">
              `pending`, `retrying`, `success`, `failed` y `filtered_out`.
            </Bullet>
            <Bullet title="Reintento manual">
              Desde el detalle del webhook podés reenviar logs `failed`.
            </Bullet>
            <Bullet title="Tu endpoint">
              Respondé 2xx apenas aceptaste el evento. El trabajo pesado conviene hacerlo en segundo plano para
              no superar el timeout.
            </Bullet>
          </Box>

          <SectionTitle id="ejemplos">7. Ejemplos de Implementación</SectionTitle>
          <SubTitle>Node.js + Express</SubTitle>
          <Body>
            Guardá el body crudo antes de parsear JSON. Si Express parsea primero, la firma HMAC no va a validar.
          </Body>
          <CodeBlock language="javascript" code={NODE_EXPRESS} />
          <SubTitle>Python</SubTitle>
          <CodeBlock language="python" code={PYTHON_VERIFY} />

          <SectionTitle id="testing">8. Testing y Debugging</SectionTitle>
          <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
            <Bullet title="Probar">
              El botón Probar de la pantalla envía un POST de prueba (`POST /api/admin/webhooks/test/{'{id}'}`).
            </Bullet>
            <Bullet title="Logs y stats">
              Abrí el detalle de la suscripción para ver fecha, evento, estado, intentos, HTTP y respuesta.
            </Bullet>
            <Bullet title="Sync de clientes">
              Si la suscripción incluye `cliente.created`, podés disparar una sincronización masiva (con dry-run).
            </Bullet>
            <Bullet title="API admin">
              Base `api/admin/webhooks`: configs, logs, stats, retry, events, filter-fields, test y sync/clientes.
            </Bullet>
          </Box>
          <SubTitle>Errores frecuentes</SubTitle>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            <Bullet title="Firma inválida">
              Estás hasheando el JSON re-serializado en lugar del body crudo, o el secret no coincide.
            </Bullet>
            <Bullet title="Timeouts">
              Tu endpoint tarda más que `timeoutMs`. Respondé 2xx y procesá después.
            </Bullet>
            <Bullet title="filtered_out">
              Las reglas de filtro no coincidieron; no es un fallo de red.
            </Bullet>
            <Bullet title="Webhook inactivo">
              Si desactivás la suscripción, los envíos pendientes se marcan como fallidos.
            </Bullet>
          </Box>

          <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #e5e7eb' }}>
            <Button variant="contained" onClick={() => navigate('/configuraciones/webhooks')} sx={{ textTransform: 'none' }}>
              Ir a la configuración de webhooks
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WebhooksDocumentacion;
